import { useState } from "react";
import { Link } from "react-router-dom";
import moment from "moment";

import PostExcerpt from "./postExcerpt";
import { decodeHtmlEntities, formatTime } from "../utils/helpers";
import UserImage from "./userImage";
import { useWindowSize } from "../partials/windowSize";
import FavouriteBtn from "./favouriteBtn";
import ShareBtn from "./shareBtn";
import ClientTheme from "../core/clientTheme";
import { favoritesEnabled } from "../config";
import NoImage from "./noImage";
import CreatorOverlay from "./gridItemOverlay";

import { Typography, Chip, Box, Container, Button } from "@mui/material";
import { Row, Col } from "react-bootstrap";
import OpenNewIcon from "@mui/icons-material/OpenInNewOutlined";
import TagIcon from "@mui/icons-material/LocalOfferOutlined";
import ThumbsUpIcon from "@mui/icons-material/ThumbUpOutlined";
import CommentIcon from "@mui/icons-material/ChatBubbleOutlineOutlined";

export default function GridItem(props) {
  const [isHovered, setIsHovered] = useState(false);
  const [imageError, setImageError] = useState(false);
  const itemClass = props.itemClass;

  // category_label may arrive as a string, an array of labels, or empty.
  // Normalise to one comma-joined string (mirrors postSingle's category logic).
  let categoryLabel = "Uncategorized";
  if (Array.isArray(props.category_label)) {
    if (props.category_label.length > 0) {
      categoryLabel = props.category_label.join(", ");
    }
  } else if (props.category_label) {
    categoryLabel = props.category_label;
  }

  const dateCreated = new Date(props.created * 1000);
  const day = dateCreated.toLocaleString("en-GB", { day: "2-digit" });
  const month = dateCreated.toLocaleString("en-GB", { month: "short" });
  const year = dateCreated.toLocaleString("en-GB", { year: "2-digit" });
  const timestamp = `${day} ${month.substring(0, 3)} ${year}`;

  const clientTheme = { ...ClientTheme() };
  const { width } = useWindowSize();

  const processEventDate = () => {
    let start = props.eventStartDate.date;
    let end = props.eventEndDate.date;

    if (start.day === end.day && start.month === end.month) {
      return (
        <Typography owner="client" variant="h2">
          {end.day}
        </Typography>
      );
    } else if (start.day !== end.day && start.month === end.month) {
      return (
        <Typography owner="client" variant={`${width < 576 ? "body2" : "h6"}`}>
          {`${start.day} - ${end.day}`}
        </Typography>
      );
    } else {
      return (
        <Typography
          owner="client"
          variant={`${width < 576 ? "body2" : "subtitle1"}`}
          weight="semiBold"
          lh={`${width < 576 ? "x-wide" : "base"}`}
        >
          {`${start.day} ${start.month.substring(0, 3)}`} <br />
          {`${end.day} ${end.month.substring(0, 3)}`}
        </Typography>
      );
    }
  };

  const processEventDay = () => {
    let start = props.eventStartDate.date;
    let end = props.eventEndDate.date;
    let startDay = props.eventStartDate.weekday;
    let endDay = props.eventEndDate.weekday;

    if (start.day === end.day && start.month === end.month) {
      return endDay;
    } else {
      return `${startDay.substring(0, 2)} - ${endDay.substring(0, 2)}`;
    }
  };

  const renderEventDate = () => {
    return (
      <Col xs={6}>
        <Container className="grid-item-event-main" variant="eventContainer">
          <Box className="grid-item-event-day p-1 d-flex justify-content-center align-items-center">
            <Typography
              owner="client"
              variant="overline"
              weight="semiBold"
              lh="base"
            >
              {processEventDay()}
            </Typography>
          </Box>
          <Box className="grid-item-event-date">{processEventDate()}</Box>
        </Container>
      </Col>
    );
  };

  const renderImageContainer = (imgColWidth, imgRatio) => {
    let ratio = "";
    switch (imgRatio) {
      case "wide":
        ratio = "ratio ratio-16x9";
        break;
      case "square":
        ratio = "ratio ratio-1x1";
        break;
      case "free":
      default:
        ratio = "";
        break;
    }
    // An <img> with no usable src renders its ALT TEXT inside the frame, so an
    // imageless card shows its own title floating where the picture should be.
    // NoImage() is an empty string for every client except equans, so that hit
    // any card whose item has no image. Keep the container — it reserves the
    // space that keeps titles and action bars aligned across a row — and just
    // leave it empty.
    const resolvedSrc = imageError ? NoImage() : props.imgSrc;
    return (
      <Col xs={imgColWidth || 12} className="grid-item-card-media-container">
        <div className={`grid-item-card-img-container ${ratio}`}>
          {props.creator && <CreatorOverlay {...props} id={props.id ?? props.nid} />}
          {resolvedSrc && (
            <img
              className="grid-item-card-img"
              src={resolvedSrc}
              alt={`${props.title}`}
              onError={() => setImageError(true)}
            />
          )}
          {/* {props.actionButtons && renderbuttons()} */}
        </div>
      </Col>
    );
  };

  const renderOverline = () => {
    return (
      <Row className="overline-container d-flex justify-content-between align-items-center">
        {props.overlineCategory && (
          <Col>
            <Row>
              {props.overlineCategoryLogo && (
                <Col
                  xs={"auto"}
                  className="d-flex flex-row align-items-center pe-0"
                >
                  <Typography
                    className="grid-item-card-overline-category-logo"
                    variant={props.overlineCategoryVariant || "caption"}
                    owner="client"
                    color="clientPrimary.main"
                    family="primary"
                    weight={props.overlineCategoryWeight || "light"}
                  >
                    {props.overlineCategoryLogo}
                  </Typography>
                </Col>
              )}
              <Col
                xs={"auto"}
                className={`d-flex flex-row align-items-center ${
                  !!props.overlineCategoryLogo && "ps-2"
                }`}
              >
                <Typography
                  className="grid-item-card-overline-category"
                  variant={props.overlineCategoryVariant || "caption"}
                  owner="client"
                  color="clientPrimary.main"
                  family="primary"
                  weight={props.overlineCategoryWeight || "light"}
                >
                  {categoryLabel}
                </Typography>
              </Col>
            </Row>
          </Col>
        )}
        {props.overlineStats && (
          <Col xs={"auto"} className="d-flex flex-row align-items-center">
            <Row>
              <Col className="pe-0">
                <Typography
                  className="grid-item-card-overline-reaction d-flex align-items-center"
                  variant={props.overlineCategoryVariant || "caption"}
                  owner="client"
                  color="clientPrimary.main"
                  family="primary"
                  weight={props.overlineCategoryWeight || "light"}
                >
                  {props.reactionCount}
                  <ThumbsUpIcon
                    className="ms-1"
                    sx={{ fontSize: "1rem", transform: "translateY(-1px)" }}
                  />
                </Typography>
              </Col>
              <Col>
                <Typography
                  className="grid-item-card-overline-comment d-flex align-items-center"
                  variant={props.overlineCategoryVariant || "caption"}
                  owner="client"
                  color="clientPrimary.main"
                  family="primary"
                  weight={props.overlineCategoryWeight || "light"}
                >
                  {props.commentCount}
                  <CommentIcon
                    className="ms-1"
                    sx={{ fontSize: "1rem", transform: "translateY(1px)" }}
                  />
                </Typography>
              </Col>
            </Row>
          </Col>
        )}
      </Row>
    );
  };

  const renderBody = () => {
    if (props.creator || props.disableBody) return "";
    return (
      <Col className="grid-item-card-description-container">
        <PostExcerpt
          className="grid-item-card-description"
          excerptColor={
            props.newsVariant === "featured"
              ? "clientText.contrastText"
              : "clientText.main"
          }
          owner="client"
          variant="body2"
          lh="wide"
          body={props.field_body}
          weight="light"
        />
      </Col>
    );
  };

  const renderActionArea = () => {
    const isFeatured = props.newsVariant === "featured";
    const isSticky = props.newsVariant === "sticky";
    const isSmallScreen = width < 576 && width > 375;

    const titleVariant = props.creator
      ? "body2"
      : props.titleVariant
        ? props.titleVariant
        : isFeatured
          ? "h4"
          : !props.event && isSmallScreen
            ? "h5"
            : "subtitle1";

    const titleColor =
      isHovered && !props.creator
        ? isFeatured
          ? "clientText.contrastText"
          : "clientPrimary.main"
        : isFeatured
          ? "clientText.contrastText"
          : "clientHeading.items";

    const titleLH = props.titleLH
      ? props.titleLH
      : isFeatured
        ? "medium"
        : "semi-wide";

    const avatarCol = props.textContainerCol ? 12 - props.textContainerCol : 3;
    const textCol =
      props.textContainerCol === "" ? "" : props.textContainerCol || 12;

    return (
      <Box
        className="grid-item-card-action-area"
        onClick={() => props.cardActionCallBack?.(props.args)}
      >
        <Box className="grid-item-content">
          <Row className="image-container">
            {props.hasImg &&
              (props.event ? (
                <Col className="event-date-container">
                  <Row className="gx-0 event-date-container-row">
                    {renderEventDate()}
                    {renderImageContainer(
                      props.imageCol || 6,
                      props.imageRatio || "square",
                    )}
                  </Row>
                </Col>
              ) : (
                renderImageContainer(
                  props.imageCol || 12,
                  props.imageRatio || "wide",
                )
              ))}
            {props.hasAvatar && (
              <Col xs={avatarCol} className="pe-0">
                <UserImage
                  className="ratio ratio-1x1"
                  owner="client"
                  sub_variant="user-image"
                  name={props.altStringAvatar || props.title}
                  image={props.user_picture}
                />
              </Col>
            )}
            <Col className="text-container" xs={textCol}>
              {(props.overlineCategory || props.overlineStats) &&
                renderOverline()}
              <Typography
                className="grid-item-card-title"
                variant={titleVariant}
                owner="client"
                color={titleColor}
                lh={titleLH}
                family={props.creator ? "primary" : "secondary"}
                weight="medium"
              >
                {decodeHtmlEntities(props.title)}
              </Typography>
              {props.creator && renderCreatorDetails()}
              {props.field_body && renderBody()}
              {renderFooter()}
              {props.cardActions && isSticky && renderCardActions()}
            </Col>
          </Row>
        </Box>
      </Box>
    );
  };

  const renderFooter = () => {
    if (props.creator || props.cardActions) return null;

    const footerContent = (
      <footer
        className={`${itemClass}__footer grid-item__footer item__footer container px-0`}
      >
        <Row className="d-flex justify-content-between mx-0 gx-0">
          <Col
            xs={props.chipWidth ?? 8}
            md={props.chipWidth ? props.chipWidth - 2 : 6}
            xxl={props.chipWidth ?? 8}
            className={"d-flex align-items-center category-chip-col"}
          >
            {props.author && renderAuthor()}
            {renderChip(categoryLabel)}
          </Col>
          {!props.event ? (
            <>
              <Col xs={"auto"} className="footer-right">
                <Row
                  className={`align-items-center gx-0 ${
                    !favoritesEnabled ? "pe-1" : ""
                  }`}
                >
                  <Col
                    xs={props.chipWidth ?? "auto"}
                    className={`timestamp pe-0 ${
                      !favoritesEnabled ? "me-1" : ""
                    }`}
                  >
                    {props.footerDate && (
                      <Col className="d-flex align-items-center">
                        <Typography
                          variant={props.authorVariant || "body2"}
                          owner="client"
                          color="clientText.main"
                          family="primary"
                          weight="regular"
                        >
                          {formatTime(props.created, true, true, true)}
                        </Typography>
                      </Col>
                    )}
                    {props.newsVariant === "featured" &&
                    clientTheme.components.card.date.featuredChip ? (
                      renderChip(props.created ? timestamp : "", "date")
                    ) : props.company_directory ? (
                      <Typography
                        variant="caption"
                        className={`timestamp__text`}
                        owner="client"
                        color="clientPrimary.main"
                      >
                        {props.created ? `Updated ${timestamp}` : ""}
                      </Typography>
                    ) : (
                      <Typography
                        variant="caption"
                        className={`timestamp__text`}
                        owner="client"
                        color="clientDate.main"
                      >
                        {props.changed ? timestamp : ""}
                      </Typography>
                    )}
                  </Col>
                  {props.shareEnabled &&
                    props.route &&
                    props.field_access_token && (
                      <ShareBtn
                        className="share-col ps-0"
                        id={props.nid || props.id}
                        title={props.title}
                        route={props.route}
                        field_access_token={props.field_access_token}
                        sharePrompt={props?.sharePrompt}
                        mode={props.newsVariant}
                      />
                    )}
                  {favoritesEnabled &&
                    !props.company_directory &&
                    !props.disableFavoriteBtn && (
                      <FavouriteBtn
                        className="favorite-col ps-0"
                        id={props.nid || props.id}
                        contentType={props.contentType}
                        title={props.title}
                        thumbnail={props.imgSrc}
                        mode={props.newsVariant}
                      />
                    )}
                </Row>
              </Col>
            </>
          ) : (
            <Col>
              <Row className="d-flex justify-content-end gx-0">
                {props.shareEnabled &&
                  props.field_access_token &&
                  props.route && (
                    <ShareBtn
                      className="share-col ps-0"
                      id={props.nid || props.id}
                      contentType={props.contentType}
                      title={props.title}
                      route={props.route}
                      field_access_token={props.field_access_token}
                      sharePrompt={props?.sharePrompt}
                      mode={props.newsVariant}
                    />
                  )}
                {favoritesEnabled ? (
                  <FavouriteBtn
                    id={props.nid || props.id}
                    contentType={props.contentType}
                    title={props.title}
                    thumbnail={props.imgSrc}
                    mode={props.newsVariant}
                  />
                ) : (
                  <Col
                    lg={"auto"}
                    className="d-none d-lg-flex align-items-center"
                  >
                    <Button variant="screenItemBtn" mode="default">
                      <OpenNewIcon
                        sx={{
                          fontSize: "0.9rem",
                        }}
                      />
                    </Button>
                  </Col>
                )}
              </Row>
            </Col>
          )}
        </Row>
      </footer>
    );

    if (props.event) {
      return <Col className="footer-container mt-auto">{footerContent}</Col>;
    }

    return (
      <Row
        className={`footer-container ${props.company_directory && !props.disableFooterMargin ? "mt-auto" : ""}`}
      >
        <Col
          className={props.company_directory ? "footer-container-redactor" : ""}
          xs={12}
        >
          {footerContent}
        </Col>
      </Row>
    );
  };

  const renderCardActions = () => {
    if (props.creator) return;
    return (
      <Col xs="auto" className="footer-container mt-auto pt-2">
        <footer
          className={`${itemClass}__footer grid-item__footer item__footer card-actions mt-auto w-100`}
        >
          <Row className="d-flex justify-content-between gx-0">
            <Col
              xs={props.chipWidth ?? "auto"}
              className="d-flex align-items-center category-chip-col"
            >
              {!props.disableCategory && renderChip(categoryLabel)}
            </Col>
            <Col xs={"auto"} className="timestamp pe-0">
              <Row className="w-100 d-flex gx-0">{props.children}</Row>
            </Col>
          </Row>
        </footer>
      </Col>
    );
  };

  const renderChip = (content, type) => {
    if (props.disableChip) {
      return <></>;
    }
    return (
      <Chip
        className="category-chip"
        variant={props.newsVariant === "featured" ? "featured" : "default"}
        owner="client"
        role="metaData"
        size="small"
        label={
          <Typography
            variant={`${clientTheme.components.chip.text.variant ?? "caption"}`}
            weight={`${clientTheme.components.chip.text.weight ?? ""}`}
            lh={`${clientTheme.components.chip.text.lh ?? ""}`}
            owner="client"
          >
            {clientTheme.components.chip.icon && type !== "date" && (
              <TagIcon className="me-1" sx={{ fontSize: "0.9rem" }} />
            )}
            {content}
          </Typography>
        }
      />
    );
  };

  const renderAuthor = () => {
    return (
      <Row>
        <Col xs={"auto"} className="d-flex align-items-center pe-0">
          <UserImage
            className="ratio ratio-1x1"
            owner="client"
            sub_variant="user-image"
            name={props.author.name}
            image={props.author.image}
          />
        </Col>
        <Col xs={"auto"} className="d-flex align-items-center ps-2">
          <Typography
            className="author-name"
            variant={props.authorVariant || "body2"}
            owner="client"
            color="clientText.main"
            family="primary"
            weight="regular"
          >
            {props.author.name}
          </Typography>
        </Col>
      </Row>
    );
  };

  const renderCreatorDetails = () => {
    const {
      author,
      changed,
      created,
      published_at,
      category_labels,
      reference,
      language,
    } = props.item;

    const processUnixTime = (unixTime) => {
      const unixDate = moment.unix(unixTime);
      const date = unixDate.format("DD[.]MM[.]YY [(]HH[:]mm[)]");
      return date;
    };

    const detailRow = (_label, _value) => {
      return (
        <Row className="detail-row">
          <Col className="d-flex" xs={4}>
            <Typography
              variant="caption"
              weight="medium"
              owner="admin"
              color="adminText.dark"
              className="label"
            >
              {_label}
            </Typography>
          </Col>
          <Col className="d-flex justify-content-end">
            <Typography
              variant="caption"
              owner="admin"
              color="adminText.main"
              className="value"
            >
              {_value}
            </Typography>
          </Col>
        </Row>
      );
    };

    return (
      <div className="grid-item-creator-details">
        {author && detailRow("Author", author.name)}
        {changed && detailRow("Modified", processUnixTime(changed))}
        {created && detailRow("Created", processUnixTime(changed))}
        {published_at !== 0 &&
          detailRow("Published", processUnixTime(published_at))}
        {category_labels !== "" && detailRow("Category", category_labels)}
        {reference && detailRow("Reference", reference?.label)}
        {language && detailRow("Language", language?.label)}
      </div>
    );
  };

  return (
    <>
      <Col
        key={`bundle-${props.bundle || "undefined"}-id-${props.nid ?? props.id}`}
        className={`grid-item-column 
          ${props.itemClass}-item-column ${
            props.newsVariant ? props.newsVariant : ""
          } 
          ${props.cardActions ? "card-actions" : ""}
          ${isHovered ? "hovered" : ""}
          ${props.creator ? "creator" : ""}
        `}
        xs={props.xs}
        sm={props.sm}
        md={props.md}
        lg={props.lg}
        xl={props.xl}
        xxl={props.xxl}
      >
        <Box
          className={`${props.itemClass}-card grid-item-card item ${
            props.newsVariant ? props.newsVariant : ""
          } 
          `}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {props.linkTo && (
            <Link
              to={
                `/${props.route}/${props.viewLink ? "view/" : ""}` +
                (props.nid ?? props.id)
              }
            >
              {renderActionArea()}
            </Link>
          )}
          {props.cardActionArea && renderActionArea()}
          {props.cardActions &&
            (props.newsVariant
              ? props.newsVariant === "sticky" ||
                props.newsVariant === "featured"
                ? ""
                : renderCardActions()
              : renderCardActions())}
        </Box>
      </Col>
    </>
  );
}
